import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Loader2, RefreshCw, Trash2 } from "lucide-react";

import {
  api,
  ApiError,
  type StorageHistoryPoint,
  type StorageHistoryRange,
  type StoragePurgeDaysResult,
  type StorageUsage,
} from "../../api";
import { FieldLabel, InfoHint } from "../../components/InfoHint";
import { Note, SettingsSection, formatBytes } from "./shared";

const RANGES: { id: StorageHistoryRange; label: string }[] = [
  { id: "24h", label: "24 hours" },
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "all", label: "All time" },
];

const PURGE_PRESETS: { days: number; label: string }[] = [
  { days: 1, label: "Last day" },
  { days: 3, label: "Last 3 days" },
  { days: 7, label: "Last week" },
  { days: 30, label: "Last month" },
];

const GiB = 1024 ** 3;

function bytesToGbField(bytes: number): string {
  if (!bytes) return "0";
  const gb = bytes / GiB;
  return gb >= 10 ? String(Math.round(gb)) : gb.toFixed(1).replace(/\.0$/, "");
}

function formatAsOf(ms: number): string {
  if (!ms) return "";
  return new Date(ms).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function gbFieldToBytes(raw: string): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * GiB);
}

function formatRangeLabel(ms: number, range: StorageHistoryRange): string {
  const d = new Date(ms);
  if (range === "24h") {
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function UsageMetric({
  label,
  value,
  detail,
  calculating,
  children,
}: {
  label: string;
  value: string | null;
  detail?: string;
  calculating: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="bf-card">
      <div className="text-xs uppercase tracking-wide text-bf-muted">{label}</div>
      {value ? (
        <div className="mt-1 flex items-center gap-2 text-2xl font-semibold text-white">
          {value}
          {calculating && (
            <Loader2
              size={16}
              className="animate-spin text-bf-muted"
              aria-label="Calculating"
            />
          )}
        </div>
      ) : (
        <div className="mt-2 flex items-center gap-2 text-sm text-bf-muted">
          {calculating ? (
            <>
              <Loader2 size={16} className="animate-spin shrink-0" aria-hidden />
              Calculating…
            </>
          ) : (
            "—"
          )}
        </div>
      )}
      {detail && <div className="text-xs text-bf-muted">{detail}</div>}
      {children}
    </div>
  );
}

function UsageBar({
  fraction,
  warn = false,
  className = "",
}: {
  fraction: number;
  warn?: boolean;
  className?: string;
}) {
  const pct = Math.max(0, Math.min(100, fraction * 100));
  return (
    <div className={"h-1.5 w-full overflow-hidden rounded-full bg-white/5 " + className}>
      <div
        className={"h-full rounded-full " + (warn ? "bg-amber-400" : "bg-bf-gradient")}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

const CHART_W = 800;
const CHART_H = 160;
const CHART_PAD = { top: 12, right: 12, bottom: 4, left: 4 };

function StorageChart({
  title,
  points,
  range,
  series,
  budgetBytes,
}: {
  title: string;
  points: StorageHistoryPoint[];
  range: StorageHistoryRange;
  series: Array<{ key: keyof StorageHistoryPoint; label: string; color: string }>;
  budgetBytes?: number;
}) {
  if (points.length < 2) return null;

  const values = points.flatMap((p) => series.map((s) => Number(p[s.key]) || 0));
  if (budgetBytes && budgetBytes > 0) values.push(budgetBytes);
  const max = Math.max(1, ...values);
  const plotW = CHART_W - CHART_PAD.left - CHART_PAD.right;
  const plotH = CHART_H - CHART_PAD.top - CHART_PAD.bottom;
  const xAt = (i: number) =>
    points.length === 1
      ? CHART_PAD.left + plotW / 2
      : CHART_PAD.left + (i / (points.length - 1)) * plotW;
  const yAt = (v: number) => CHART_PAD.top + plotH - (v / max) * plotH;

  const yTicks = [0, 0.5, 1].map((t) => ({
    y: yAt(max * t),
    label: formatBytes(max * t),
  }));

  return (
    <div className="bf-card p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-white">{title}</h3>
        {series.length > 1 && (
          <div className="flex flex-wrap gap-3 text-xs text-bf-muted">
            {series.map((s) => (
              <span key={s.key} className="inline-flex items-center gap-1.5">
                <span className="h-1.5 w-3 rounded-full" style={{ background: s.color }} />
                {s.label}
              </span>
            ))}
          </div>
        )}
      </div>
      <svg
        viewBox={`0 0 ${CHART_W} ${CHART_H}`}
        className="h-40 w-full"
        role="img"
        aria-label={title}
      >
        {yTicks.map((tick) => (
          <line
            key={tick.y}
            x1={CHART_PAD.left}
            y1={tick.y}
            x2={CHART_W - CHART_PAD.right}
            y2={tick.y}
            stroke="rgb(255 255 255 / 0.08)"
            strokeWidth={1}
          />
        ))}
        {budgetBytes && budgetBytes > 0 ? (
          <line
            x1={CHART_PAD.left}
            y1={yAt(budgetBytes)}
            x2={CHART_W - CHART_PAD.right}
            y2={yAt(budgetBytes)}
            stroke="rgb(251 191 36 / 0.7)"
            strokeWidth={1.5}
            strokeDasharray="6 4"
          />
        ) : null}
        {series.map((s) => {
          const line = points
            .map((p, i) => `${i === 0 ? "M" : "L"} ${xAt(i)} ${yAt(Number(p[s.key]) || 0)}`)
            .join(" ");
          return (
            <path
              key={s.key}
              d={line}
              fill="none"
              stroke={s.color}
              strokeWidth={2.5}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          );
        })}
      </svg>
      <div className="mt-2 flex justify-between text-[10px] text-bf-muted sm:text-xs">
        <span>{formatRangeLabel(points[0].ts_ms, range)}</span>
        <span>{formatRangeLabel(points[points.length - 1].ts_ms, range)}</span>
      </div>
      {budgetBytes && budgetBytes > 0 ? (
        <p className="mt-1 text-[10px] text-bf-muted">
          Dashed line is the storage limit ({formatBytes(budgetBytes)}).
        </p>
      ) : null}
    </div>
  );
}

function StoreRow({
  store,
  maxBytes,
}: {
  store: StorageUsage["stores"][number];
  maxBytes: number;
}) {
  const [open, setOpen] = useState(false);
  const hasCameras = store.cameras.length > 0;

  return (
    <div className="border-t border-white/5 py-2 first:border-t-0">
      <button
        type="button"
        onClick={() => hasCameras && setOpen((v) => !v)}
        className="flex w-full items-center gap-3 text-left"
      >
        {hasCameras ? (
          open ? (
            <ChevronDown size={15} className="shrink-0 text-bf-muted" />
          ) : (
            <ChevronRight size={15} className="shrink-0 text-bf-muted" />
          )
        ) : (
          <span className="w-[15px] shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <span className="truncate text-sm font-medium text-bf-text">{store.store}</span>
            <span className="shrink-0 text-sm text-bf-text">{formatBytes(store.bytes)}</span>
          </div>
          <div className="mt-1 flex items-center gap-2">
            <UsageBar fraction={maxBytes ? store.bytes / maxBytes : 0} />
            <span className="shrink-0 text-xs text-bf-muted">
              {store.objects.toLocaleString()} item{store.objects === 1 ? "" : "s"}
            </span>
          </div>
        </div>
      </button>

      {open && hasCameras && (
        <div className="ml-6 mt-2 space-y-1.5">
          {store.cameras.map((c) => (
            <div key={c.camera_id} className="min-w-0">
              <div className="flex items-center justify-between gap-3">
                <span className="truncate text-xs text-bf-muted">{c.name}</span>
                <span className="shrink-0 text-xs text-bf-muted">{formatBytes(c.bytes)}</span>
              </div>
              <UsageBar
                fraction={store.bytes ? c.bytes / store.bytes : 0}
                className="mt-1"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function StorageSection() {
  const qc = useQueryClient();
  const [range, setRange] = useState<StorageHistoryRange>("30d");
  const [limitGb, setLimitGb] = useState("0");
  const [projectGb, setProjectGb] = useState("0");
  const [retention, setRetention] = useState("30");
  const [clipHours, setClipHours] = useState("6");
  const [backupDays, setBackupDays] = useState("3");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [purgeDays, setPurgeDays] = useState("1");
  const [purgeConfirming, setPurgeConfirming] = useState(false);
  const [purgeResult, setPurgeResult] = useState<StoragePurgeDaysResult | null>(null);
  const [purgeError, setPurgeError] = useState<string | null>(null);

  const policyQ = useQuery({
    queryKey: ["storage-policy"],
    queryFn: api.admin.storagePolicy,
  });
  const usage = useQuery<StorageUsage>({
    queryKey: ["storage-usage"],
    queryFn: api.admin.storage,
  });
  const history = useQuery({
    queryKey: ["storage-history", range],
    queryFn: () => api.admin.storageHistory(range),
  });

  useEffect(() => {
    const policy = policyQ.data?.policy ?? usage.data?.policy;
    if (!policy) return;
    setLimitGb(bytesToGbField(policy.max_bytes));
    setProjectGb(bytesToGbField(policy.project_max_bytes ?? 0));
    setRetention(String(policy.retention_days));
    setClipHours(String(policy.clip_retention_hours));
    setBackupDays(String(policy.qdrant_snapshot_keep_days ?? 3));
  }, [policyQ.data, usage.data]);

  const save = useMutation({
    mutationFn: () =>
      api.admin.updateStoragePolicy({
        max_bytes: gbFieldToBytes(limitGb),
        project_max_bytes: gbFieldToBytes(projectGb),
        retention_days: Math.max(1, Math.min(3650, Number(retention) || 30)),
        clip_retention_hours: Math.max(1, Math.min(72, Number(clipHours) || 6)),
        qdrant_snapshot_keep_days: Math.max(1, Math.min(365, Number(backupDays) || 3)),
      }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      qc.invalidateQueries({ queryKey: ["storage-policy"] });
      qc.invalidateQueries({ queryKey: ["storage-usage"] });
      qc.invalidateQueries({ queryKey: ["storage-history"] });
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : "Could not save storage settings."),
  });

  const purgeRecent = useMutation({
    mutationFn: (days: number) => api.admin.purgeStorageDays(days),
    onSuccess: (r) => {
      setPurgeError(null);
      setPurgeResult(r);
      setPurgeConfirming(false);
      qc.invalidateQueries({ queryKey: ["storage-usage"] });
      qc.invalidateQueries({ queryKey: ["storage-history"] });
      qc.invalidateQueries({ queryKey: ["search"] });
    },
    onError: (e: unknown) =>
      setPurgeError(
        e instanceof ApiError ? e.message : "Could not delete storage for that window.",
      ),
  });

  const policy = policyQ.data?.policy ?? usage.data?.policy;
  if (policyQ.isLoading && !policy) {
    return (
      <SettingsSection title="Storage">
        <div className="bf-card text-sm text-bf-muted">Loading storage settings…</div>
      </SettingsSection>
    );
  }
  if ((policyQ.isError && !policy) || !policy) {
    return (
      <SettingsSection title="Storage">
        <Note tone="error">Could not read storage settings. Please try again.</Note>
      </SettingsSection>
    );
  }

  const data = usage.data;
  const maxStore = Math.max(1, ...(data?.stores.map((s) => s.bytes) ?? [1]));
  const budget = policy.max_bytes ?? 0;
  const overBudget = Boolean(data && budget > 0 && data.total_bytes >= budget * 0.95);
  const points = history.data?.points ?? [];
  const retentionCap = Math.max(1, Number(policy.retention_days) || 30);
  const selectedPurgeDays = Math.max(
    1,
    Math.min(retentionCap, Math.floor(Number(purgeDays) || 1)),
  );
  const purgePresets = PURGE_PRESETS.filter((p) => p.days <= retentionCap);

  return (
    <SettingsSection
      title="Storage"
      description="How long detections and backups are kept, and the most disk they may use."
      hint={
        <>
          Detections older than the retention days are deleted. If a detection
          or project limit fills up sooner, the oldest detections are removed
          to stay under it. Video clips expire on their own hour budget. Index
          backups keep only the newest N daily dumps.
        </>
      }
      actions={
        <button
          type="button"
          onClick={() => {
            usage.refetch();
            history.refetch();
          }}
          className="bf-button bg-white/10 bg-none disabled:opacity-40"
          disabled={usage.isFetching}
        >
          <RefreshCw
            size={15}
            className={"mr-2 " + (usage.isFetching ? "animate-spin" : "")}
          />
          Refresh
        </button>
      }
    >
      {data?.warning && (
        <Note tone="warning">
          Storage is at the limit. About {data.warning.history_days_kept} days of
          history are kept; the goal is {data.warning.retention_days} days.
          Oldest detections are being removed to stay under{" "}
          {formatBytes(data.warning.max_bytes)}
          {data.warning.limit === "project"
            ? " (project limit)"
            : data.warning.limit === "both"
              ? " (detection and project limits)"
              : " (detection limit)"}
          . Add disk, raise the limit, or reduce how much is captured.
        </Note>
      )}
      {error && <Note tone="error">{error}</Note>}
      {saved && !save.isPending && <Note tone="success">Storage settings saved.</Note>}

      <div className="bf-card space-y-3">
        <h3 className="text-sm font-semibold text-white">Limits</h3>
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <FieldLabel hint="Maximum space for detection frames and video clips. 0 means no limit.">
              Detection limit (GB)
            </FieldLabel>
            <input
              className="bf-input"
              type="number"
              min={0}
              step="0.1"
              value={limitGb}
              onChange={(e) => {
                setLimitGb(e.target.value);
                setSaved(false);
              }}
            />
          </div>
          <div>
            <FieldLabel hint="Detections older than this are deleted, unless a size limit fills first.">
              Keep detections (days)
            </FieldLabel>
            <input
              className="bf-input"
              type="number"
              min={1}
              max={3650}
              value={retention}
              onChange={(e) => {
                setRetention(e.target.value);
                setSaved(false);
              }}
            />
          </div>
          <div>
            <FieldLabel hint="Parked so the page can play or download. Removed after this many hours.">
              Keep video clips (hours)
            </FieldLabel>
            <input
              className="bf-input"
              type="number"
              min={1}
              max={72}
              value={clipHours}
              onChange={(e) => {
                setClipHours(e.target.value);
                setSaved(false);
              }}
            />
          </div>
          <div>
            <FieldLabel hint="Daily search-index dumps. Only the newest this many days are kept.">
              Index backups (days)
            </FieldLabel>
            <input
              className="bf-input"
              type="number"
              min={1}
              max={365}
              value={backupDays}
              onChange={(e) => {
                setBackupDays(e.target.value);
                setSaved(false);
              }}
            />
          </div>
          <div>
            <FieldLabel hint="Cap for detection frames, clips, index backups, and database dumps together. 0 means no extra cap.">
              Project limit (GB)
            </FieldLabel>
            <input
              className="bf-input"
              type="number"
              min={0}
              step="1"
              value={projectGb}
              onChange={(e) => {
                setProjectGb(e.target.value);
                setSaved(false);
              }}
            />
          </div>
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            className="bf-button disabled:opacity-40"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div
        className={
          "bf-card relative space-y-3 " +
          (purgeRecent.isPending ? "pointer-events-none" : "")
        }
        aria-busy={purgeRecent.isPending}
      >
        {purgeRecent.isPending && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] bg-black/50">
            <div className="flex items-center gap-2 text-sm text-bf-muted">
              <Loader2 size={18} className="animate-spin shrink-0" aria-hidden />
              Deleting last {selectedPurgeDays} day
              {selectedPurgeDays === 1 ? "" : "s"}…
            </div>
          </div>
        )}
        <div
          className={
            "space-y-3 " + (purgeRecent.isPending ? "opacity-40 grayscale" : "")
          }
        >
          <h3 className="text-sm font-semibold text-white">Delete recent storage</h3>
          <p className="text-xs text-bf-muted">
            Permanently remove detections, video clips, search index entries, and
            dated backups from the last N days (UTC). Cap is the keep-detections
            setting ({retentionCap} day{retentionCap === 1 ? "" : "s"}).
          </p>
          <div className="flex flex-wrap gap-2">
            {purgePresets.map((p) => (
              <button
                key={p.days}
                type="button"
                disabled={purgeRecent.isPending}
                onClick={() => {
                  setPurgeDays(String(p.days));
                  setPurgeConfirming(false);
                  setPurgeResult(null);
                  setPurgeError(null);
                }}
                className={
                  "rounded-lg px-3 py-1.5 text-sm transition disabled:cursor-not-allowed " +
                  (selectedPurgeDays === p.days
                    ? "bg-red-600/30 text-white ring-1 ring-red-500/60"
                    : "bg-white/5 text-bf-muted hover:bg-white/10 hover:text-bf-text")
                }
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-36">
              <FieldLabel hint="Custom window in whole days, up to keep-detections.">
                Custom (days)
              </FieldLabel>
              <input
                className="bf-input"
                type="number"
                min={1}
                max={retentionCap}
                value={purgeDays}
                disabled={purgeRecent.isPending}
                onChange={(e) => {
                  setPurgeDays(e.target.value);
                  setPurgeConfirming(false);
                  setPurgeResult(null);
                  setPurgeError(null);
                }}
              />
            </div>
            {!purgeConfirming ? (
              <button
                type="button"
                className="bf-button bg-red-600/80 bg-none hover:bg-red-500 disabled:opacity-40"
                disabled={purgeRecent.isPending}
                onClick={() => {
                  setPurgeResult(null);
                  setPurgeError(null);
                  setPurgeConfirming(true);
                }}
              >
                <Trash2 size={15} className="mr-2" />
                Delete last {selectedPurgeDays} day
                {selectedPurgeDays === 1 ? "" : "s"}
              </button>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-bf-muted">
                  Delete everything stored in the last {selectedPurgeDays} day
                  {selectedPurgeDays === 1 ? "" : "s"}?
                </span>
                <button
                  type="button"
                  className="text-xs text-bf-muted hover:text-bf-text disabled:opacity-40"
                  disabled={purgeRecent.isPending}
                  onClick={() => setPurgeConfirming(false)}
                >
                  cancel
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded bg-red-600/80 px-2 py-1 text-xs text-white hover:bg-red-500 disabled:opacity-40"
                  disabled={purgeRecent.isPending}
                  onClick={() => purgeRecent.mutate(selectedPurgeDays)}
                >
                  {purgeRecent.isPending ? (
                    <>
                      <Loader2 size={12} className="animate-spin" aria-hidden />
                      Deleting…
                    </>
                  ) : (
                    "Confirm"
                  )}
                </button>
              </div>
            )}
          </div>
          {purgeError && <Note tone="error">{purgeError}</Note>}
          {purgeResult && (
            <Note tone="success">
              Deleted {purgeResult.start_date} → {purgeResult.end_date}:{" "}
              {purgeResult.frames_deleted.toLocaleString()} photos,{" "}
              {purgeResult.clips_deleted.toLocaleString()} clips,{" "}
              {purgeResult.snapshots_deleted.toLocaleString()} index backups,{" "}
              {purgeResult.pg_backups_deleted.toLocaleString()} database dumps
              {purgeResult.qdrant_tracks_cleared ? ", and matching search records" : ""}.
            </Note>
          )}
        </div>
      </div>

      {usage.isError && (
        <Note tone="error">Could not read current storage usage. Settings still apply.</Note>
      )}
      <div
        className="grid gap-3 sm:grid-cols-3"
        aria-busy={usage.isFetching && !data}
      >
        <UsageMetric
          label="Total used"
          value={data ? formatBytes(data.total_bytes) : null}
          detail={
            data?.as_of_ms ? `as of ${formatAsOf(data.as_of_ms)}` : undefined
          }
          calculating={usage.isFetching && !data}
        >
          {data && budget > 0 && (
            <>
              <div className="mt-2 text-xs text-bf-muted">
                {formatBytes(data.total_bytes)} of {formatBytes(budget)}
              </div>
              <UsageBar
                fraction={data.total_bytes / budget}
                warn={overBudget}
                className="mt-1"
              />
            </>
          )}
        </UsageMetric>
        <UsageMetric
          label="Snapshots"
          value={data ? formatBytes(data.buckets.frames.bytes) : null}
          detail={
            data && data.buckets.frames.objects > 0
              ? `${data.buckets.frames.objects.toLocaleString()} items`
              : undefined
          }
          calculating={usage.isFetching && !data}
        />
        <UsageMetric
          label="Video clips"
          value={data ? formatBytes(data.buckets.clips.bytes) : null}
          detail={
            data && data.buckets.clips.objects > 0
              ? `${data.buckets.clips.objects.toLocaleString()} items`
              : undefined
          }
          calculating={usage.isFetching && !data}
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {RANGES.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => setRange(id)}
            className={
              "rounded-lg px-3 py-1.5 text-sm transition " +
              (range === id
                ? "bg-bf-violet/30 text-white ring-1 ring-bf-violet/60"
                : "bg-white/5 text-bf-muted hover:bg-white/10 hover:text-bf-text")
            }
          >
            {label}
          </button>
        ))}
      </div>

      {history.isError && <Note tone="error">Could not load usage history.</Note>}
      {points.length >= 2 ? (
        <>
          <StorageChart
            title="Storage used over time"
            points={points}
            range={range}
            budgetBytes={budget}
            series={[{ key: "total_bytes", label: "Total", color: "rgb(192 38 211)" }]}
          />
          <StorageChart
            title="Snapshots and video clips"
            points={points}
            range={range}
            series={[
              { key: "frames_bytes", label: "Snapshots", color: "rgb(192 38 211)" },
              { key: "clips_bytes", label: "Video clips", color: "rgb(129 140 248)" },
            ]}
          />
        </>
      ) : (
        <p className="text-sm text-bf-muted">
          Usage over time appears after the next few hourly samples.
        </p>
      )}

      <div className="bf-card">
        <h3 className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-white">
          By store &amp; camera
          <InfoHint>
            Current snapshot only. The charts above are the history.
          </InfoHint>
        </h3>
        {!data ? (
          <p className="flex items-center gap-2 py-3 text-sm text-bf-muted">
            {usage.isFetching ? (
              <>
                <Loader2 size={16} className="animate-spin shrink-0" aria-hidden />
                Calculating usage…
              </>
            ) : (
              "Usage not available."
            )}
          </p>
        ) : !data.stores_ready ? (
          <p className="py-3 text-sm text-bf-muted">
            Per-store sizes update once an hour.
          </p>
        ) : data.stores.length === 0 ? (
          <p className="py-3 text-sm text-bf-muted">Nothing stored yet.</p>
        ) : (
          data.stores.map((s) => <StoreRow key={s.store} store={s} maxBytes={maxStore} />)
        )}
        {data && data.other_bytes > 0 && (
          <div className="mt-2 flex items-center justify-between border-t border-white/5 pt-2 text-xs text-bf-muted">
            <span>Other (alert crops &amp; unattributed)</span>
            <span>{formatBytes(data.other_bytes)}</span>
          </div>
        )}
      </div>
    </SettingsSection>
  );
}
